import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";

const BASE_URL = process.env.REACT_APP_API_URL;

const getHeaders = () => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${localStorage.getItem("jwt_token")}`,
});

/* ================= VIDEO QUIZ ================= */

export const submitVideoQuiz = createAsyncThunk(
  "quiz/submitVideoQuiz",
  async (payload, thunkAPI) => {
    try {
      const response = await axios.post(`${BASE_URL}/video/quiz`, payload, {
        headers: getHeaders(),
      });
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error.response?.data || "Video quiz submission failed"
      );
    }
  }
);

/* ================= CARD QUIZ ================= */

export const submitCardQuiz = createAsyncThunk(
  "quiz/submitCardQuiz",
  async (payload, thunkAPI) => {
    try {
      const response = await axios.post(`${BASE_URL}/card/quiz`, payload, {
        headers: getHeaders(),
      });
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error.response?.data || "Card quiz submission failed"
      );
    }
  }
);

/* ================= CARDS ================= */

export const fetchCardsByOrganization = createAsyncThunk(
  "cards/fetchCardsByOrganization",
  async (orgId, thunkAPI) => {
    try {
      const response = await axios.get(
        `${BASE_URL}/cards/${orgId}/organization`,
        { headers: getHeaders() }
      );
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error.response?.data || "Fetching cards failed"
      );
    }
  }
);

/* ================= QUIZZES ================= */

export const fetchQuizzes = createAsyncThunk(
  "quiz/fetchQuizzes",
  async (orgId, thunkAPI) => {
    try {
      const response = await axios.get(
        `${BASE_URL}/quiz/${orgId}/organization`,
        { headers: getHeaders() }
      );
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error.response?.data || "Fetching quizzes failed"
      );
    }
  }
);

/* ================= USER ANSWERS ================= */

export const postUserAnswer = createAsyncThunk(
  "quiz/postUserAnswer",
  async (payload, thunkAPI) => {
    try {
      const response = await axios.post(`${BASE_URL}/user-answers`, payload, {
        headers: getHeaders(),
      });
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error.response?.data || "Submitting user answers failed"
      );
    }
  }
);


export const fetchFormQuiz = createAsyncThunk(
  "quiz/fetchFormQuiz",
  async (card_number, thunkAPI) => {
    try {
      const response = await axios.get(
        `${BASE_URL}/form/quiz/${card_number}`,
      );
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error.response?.data || "Fetching quizzes failed"
      );
    }
  }
);
/* ================= SLICE ================= */

const initialState = {
  videoQuiz: {
    loading: false,
    data: null,
    error: null,
  },
  cardQuiz: {
    loading: false,
    data: null,
    error: null,
  },
  cards: {
    loading: false,
    data: [],
    error: null,
  },
  quizzes: {
    loading: false,
    data: [],
    error: null,
  },
  userAnswers: {
    loading: false,
    data: null,
    error: null,
  },
  formQuizzes: {
    loading: false,
    data: [],
    error: null,
  },
  selectedQuiz: null,
};

const quizzesSlice = createSlice({
  name: "quizzes",
  initialState,
  reducers: {
    resetVideoQuizState: (state) => {
      state.videoQuiz = { loading: false, data: null, error: null };
    },
    resetCardQuizState: (state) => {
      state.cardQuiz = { loading: false, data: null, error: null };
    },
    resetCardsState: (state) => {
      state.cards = { loading: false, data: [], error: null };
    },
    resetUserAnswersState: (state) => {
      state.userAnswers = { loading: false, data: null, error: null };
    },
    setSelectedQuiz: (state, action) => {
      state.selectedQuiz = action.payload;
    },
    resetSelectedQuiz: (state) => {
      state.selectedQuiz = null;
    },
  },
  extraReducers: (builder) => {
    builder

      /* ===== VIDEO QUIZ ===== */
      .addCase(submitVideoQuiz.pending, (state) => {
        state.videoQuiz.loading = true;
        state.videoQuiz.data = null;
        state.videoQuiz.error = null;
      })
      .addCase(submitVideoQuiz.fulfilled, (state, action) => {
        state.videoQuiz.loading = false;
        state.videoQuiz.data = action.payload;
      })
      .addCase(submitVideoQuiz.rejected, (state, action) => {
        state.videoQuiz.loading = false;
        state.videoQuiz.data = null;
        state.videoQuiz.error = action.payload;
      })

      /* ===== CARD QUIZ ===== */
      .addCase(submitCardQuiz.pending, (state) => {
        state.cardQuiz.loading = true;
        state.cardQuiz.data = null;
        state.cardQuiz.error = null;
      })
      .addCase(submitCardQuiz.fulfilled, (state, action) => {
        state.cardQuiz.loading = false;
        state.cardQuiz.data = action.payload;
      })
      .addCase(submitCardQuiz.rejected, (state, action) => {
        state.cardQuiz.loading = false;
        state.cardQuiz.data = null;
        state.cardQuiz.error = action.payload;
      })

      /* ===== FETCH CARDS ===== */
      .addCase(fetchCardsByOrganization.pending, (state) => {
        state.cards.loading = true;
        state.cards.error = null;
      })
      .addCase(fetchCardsByOrganization.fulfilled, (state, action) => {
        state.cards.loading = false;
        state.cards.data = action.payload?.data || [];
      })
      .addCase(fetchCardsByOrganization.rejected, (state, action) => {
        state.cards.loading = false;
        state.cards.error = action.payload;
      })

      /* ===== FETCH QUIZZES ===== */
      .addCase(fetchQuizzes.pending, (state) => {
        state.quizzes.loading = true;
      })
      .addCase(fetchQuizzes.fulfilled, (state, action) => {
        state.quizzes.loading = false;

        const raw = action.payload?.data || [];

        state.quizzes.data = raw.map((item, index) => {
          const key = Object.keys(item)[0];
          const quiz = item[key];

          return {
            id: key,
            video_id: quiz?.video_id || null,
            card_id: quiz?.card_id || null,
            displayName: quiz?.name || `Quiz ${index + 1}`,
            displayName_dk: quiz?.name_dk || `Quiz ${index + 1}`,
            questions: quiz?.questions || [],
            expire_date: quiz?.expire_date || null,
            correct_answers: quiz?.correct_answers || 1,
            attempt: quiz?.attempt || 0,
            quiz_id: quiz?.quiz_id || null
          };
        });
      })
      .addCase(fetchQuizzes.rejected, (state, action) => {
        state.quizzes.loading = false;
        state.quizzes.error = action.payload;
      })

      /* ===== USER ANSWERS ===== */
      .addCase(postUserAnswer.pending, (state) => {
        state.userAnswers.loading = true;
        state.userAnswers.data = null;
        state.userAnswers.error = null;
      })
      .addCase(postUserAnswer.fulfilled, (state, action) => {
        state.userAnswers.loading = false;
        state.userAnswers.data = action.payload;
      })
      .addCase(postUserAnswer.rejected, (state, action) => {
        state.userAnswers.loading = false;
        state.userAnswers.data = null;
        state.userAnswers.error = action.payload;
      })

      .addCase(fetchFormQuiz.pending, (state) => {
        state.formQuizzes.loading = true;
      })
      .addCase(fetchFormQuiz.fulfilled, (state, action) => {
        state.formQuizzes.loading = false;

        const raw = action.payload?.data || [];

        state.formQuizzes.data = raw.map((item, index) => {
          const key = Object.keys(item)[0];
          const quiz = item[key];
          return {
            id: key,
            video_id: quiz?.video_id || null,
            card_id: quiz?.card_id || null,
            displayName: quiz?.name || `Quiz ${index + 1}`,
            displayName_dk: quiz?.name_dk || `Quiz ${index + 1}`,
            questions: quiz?.questions || [],
            expire_date: quiz?.expire_date || null,
            correct_answers: quiz?.correct_answers || 1,
            attempt: quiz?.attempt || 0,
            quiz_id: quiz?.quiz_id || null
          };
        });
      })
      .addCase(fetchFormQuiz.rejected, (state, action) => {
        state.formQuizzes.loading = false;
        state.formQuizzes.error = action.payload;
      })
  },
});

export const {
  resetVideoQuizState,
  resetCardQuizState,
  resetCardsState,
  resetUserAnswersState,
  setSelectedQuiz,
  resetSelectedQuiz,
} = quizzesSlice.actions;

export default quizzesSlice.reducer;
